import { flow /*, observable, computed, action */ } from "mobx"
import { rootStore } from "store/RootStore"
import { TraxisCpeIdMixin } from "store/api/mixin/TraxisCpeIdMixin"
// MTVW-585: 1 retry
import { fetchWithRetry, DEFAULT_RETRIES, DEFAULT_TIMEOUT } from "./FetchWithRetry"
import { alias, list, object, /*createModelSchema, getDefaultModelSchema,*/ serializable, deserialize, primitive } from "serializr"
import { moment } from "store/util/moment"
import { urlFixHttpProtocolMismatch } from "store/util/urlFixHttpProtocolMismatch"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { getQlHeaders } from "store/qlapi/QlHeaders"

import { getCoCoModule } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

const nDash = "\u2013"

const STD_HEADERS = {
   Accept: "application/json",
   // no-cache would not generate 304 on all browsers
   //'pragma': 'no-cache', 'cache-control': 'no-cache'
}

/*
class Wrapper {
	constructor(handler) {
	  this.handler = handler;
	  this.seen = new WeakMap();
	}
 
	deepProxy(target) {
	  // primitives are not wrapped
	  if (typeof target !== "object" || target === null) return target;
 
	  // avoid re-wrapping the same wasm object
	  if (this.seen.has(target)) return this.seen.get(target);
 
	  const proxy = new Proxy(target, {
		 get: (obj, prop, receiver) => {
			if (!(prop in obj)) {
				if (prop === "dateAndTime") {
					const start = obj.start ?? obj.startMillis
					const end = obj.end ?? obj.endMillis
					const val = moment(start).format(`dd. DD.MM.YYYY, HH:mm`) + nDash + moment(end).format(`HH:mm`)
					console.debug("Intercepted dateAndTime", obj, receiver, val)
					return val
				}
				else if (prop === "eventTime") {
					const val = moment(obj.start).format(`HH:mm`) + nDash + moment(obj.end).format(`HH:mm`)
					console.debug("Intercepted eventTime", obj, receiver, val)
					return val
				}
				else if (prop === "durationInMinutes") {
					const startTs = Number(obj.startMillis ?? obj.start_millis ?? null)
					const endTs = Number(obj.endMillis ?? obj.end_millis ?? null)
					val = Math.round(moment.duration(moment(endTs).diff(moment(startTs))).asMinutes())
					console.debug("Intercepted durationInMinutes", obj, receiver, val)
					return val
				}
				// MTVW-1016: for episodes / GenreTile
				else if (prop === "AvailabilityDurationInMinutes") {
					const startTs = Number(obj.startMillis ?? obj.start_millis ?? null)
					const endTs = Number(obj.endMillis ?? obj.end_millis ?? null)
					val = Math.round(moment.duration(moment(endTs).diff(moment(startTs))).asMinutes())
					console.debug("Intercepted AvailabilityDurationInMinutes", obj, receiver, val)
					return val
				}
				else if (prop === "eventType") {
					const timeCurr = moment().utc().valueOf()
					const startTs = Number(obj.startMillis ?? obj.start_millis)
					const endTs = Number(obj.endMillis ?? obj.end_millis)
					const val = startTs <= timeCurr && endTs > timeCurr ? "c" : startTs >= timeCurr ? "n" : "p"
					console.debug("Intercepted eventType", obj, receiver, val)
					return val
				}
				else if (prop === "durationInPercent") {
					const timeCurr = moment().utc().valueOf()
					const startTs = Number(obj.startMillis ?? obj.start_millis)
					const endTs = Number(obj.endMillis ?? obj.end_millis)
					const eventType = startTs <= timeCurr && endTs > timeCurr ? "c" : startTs >= timeCurr ? "n" : "p"
					if (eventType !== "c") return 0
					const val = parseFloat(((100 * (timeCurr - startTs)) / (endTs - startTs)).toFixed(2))
					console.debug("Intercepted durationInPercent", obj, receiver, val)
					return val
				}
				else if (prop === "AvailabilityEndTs") {
					const val = Number(obj.endMillis ?? obj.end_millis ?? null)
					console.debug("Intercepted AvailabilityEndTs", obj, receiver, val)
					return val
				}
				else if (prop === "AvailabilityStartTs") {
					const val = Number(obj.startMillis ?? obj.start_millis ?? null)
					console.debug("Intercepted AvailabilityStartTs", obj, receiver, val)
					return val
				}
				else if (prop === "startDateFormat") {
					return (format) => {
						const startTs = Number(obj.startMillis ?? obj.start_millis)
						const val = moment(startTs).format(format)
						console.debug("Intercepted startDateFormat", obj, receiver, val)
						return val
					 }
				}
				// MTVW-1016: for episodes / GenreTile	
				else if (prop === "AvailabilityStartDateFormat") {
					return (format) => {
						const startTs = Number(obj.startMillis ?? obj.start_millis)
						const val = moment(startTs).format(format)
						console.debug("Intercepted AvailabilityStartDateFormat", obj, receiver, val)
						return val
					 }
				}
				else console.debug(`Missing property: ${String(prop)}`, target)
			}
			const rawValue = Reflect.get(obj, prop, receiver);
 
			// call class-provided interception logic
			const intercepted =
			  this.handler.get?.(obj, prop, rawValue, receiver) ?? rawValue;
 
			// recursive wrap using the same instance of Wrapper
			return this.deepProxy(intercepted);
		 },
 
		 set: (obj, prop, value, receiver) => {
			return (
			  this.handler.set?.(obj, prop, value, receiver) ??
			  Reflect.set(obj, prop, value, receiver)
			);
		 }
	  });
 
	  this.seen.set(target, proxy);
	  return proxy;
	}
}
*/

export class SearchService extends TraxisCpeIdMixin {
   get xHeaders() {
      // make a copy of STD_HEADERS, otherwise STD_HEADERS would be changed!
      const headers = {}
      Object.assign(headers, STD_HEADERS)
      Object.assign(headers, getQlHeaders())
      return headers
   }

   version = flow(function* () {
      // eslint-disable-next-line no-unused-vars
		let result = yield CoCo.apiFetchSearchVersionRaw()
		result = JSON.parse(result)
      return `${result["build_tag"]} (${result["build_date"]})`
   })

   overviewAsync = flow(function* (query, includeAdult = false, lite = true) {
		try {
			const start = window.performance.now()
			const result = yield CoCo.search(query, includeAdult, lite)
			result.top = result.getTopMatchesRaw()
			if (typeof result.top === "string") result.top = JSON.parse(result.top)
			result.sports = result.getSportsMatchesRaw()
			if (typeof result.sports === "string") result.sports = JSON.parse(result.sports)
			result.recordings = result.getRecordingsMatchesRaw()
			if (typeof result.recordings === "string") result.recordings = JSON.parse(result.recordings)
			// CoCo Marker FIX (TODO: Handling of CoCo events)
			for (let i = 0; i < result.recordings.length; i++) {
				// CoCo TODO: Handling of CoCo events, Proxy trial
				rootStore.page.Recordings.Manager.addCocoMarker(result.recordings[i])
			}

			result.actors = result.crew
			/*
			const wrapper = new Wrapper({
				get(obj, prop, value) {
					//console.debug("GET", prop, value)
				
					if (prop === "calculate") {
						return (...args) => {
						console.debug("Intercepted calculate()")
						return value(...args) * 2
						};
					}

					return value
				}
			})
			 
			const wrapped = wrapper.deepProxy(result)
			wrapped.recordings = wrapper.deepProxy(wrapped.recordings)
			*/

			//console.debug("overviewAsync top %o, top1 %o, sports %o, recordings %o", top, top1, sports, recordings)
			// search renames actors to crew (and doesn't return genres and channels?)
			console.debug("overviewDuration", window.performance.now() - start)
			console.debug("overviewAsync %o", result/*, result1*/)
			return result
		}
		catch (e) {
			console.error("overviewAsync", e)
			return {
				actors: [], channels: [], genres: [], movies: [], recordings: [], series: [], sports: [], top: []
			}
		}
   })

	// requires exact match, otherwise 404
   infoActorAsync = flow(function* (query) {
      // exception for 404 might be generated
      try {
			const result = yield CoCo.searchByPerson(query, null, false)
			let contents = result.getContentsRaw()
			// CoCo returns string instead of object, need to convert to object
         if (typeof contents === "string") contents = JSON.parse(contents)
			result.contents = contents
			console.debug("infoActorAsync result %o", result)
         //this.actor = result
         return result
      }
		 catch (e) {
			console.error("infoActorAsync", e)
         return {}
      }
   })

   // requires exact match, otherwise 404
   infoGenreAsync = flow(function* (query) {
      if (!query) debugger
      //console.debug("infoGenreAsync query %s", query)
      // exception for 404 might be generated
      try {
         const params = query.split("?")
			const result = yield CoCo.searchByGenre(params[0], null, false)
			let contents = result.getContentsRaw()
			// CoCo returns string instead of object, need to convert to object
         if (typeof contents === "string") contents = JSON.parse(contents)
			result.contents = contents
         console.debug("infoGenreAsync query %s, result %o, len %s", query, result, result.contents.length)
			return result
      }
		catch (e) {
			console.error("infoGenreAsync", e)
         return {}
      }
   })

   // requires exact match, otherwise 404
   searchActorInGenreAsync = flow(function* (actor, genre) {
      // exception for 404 might be generated
      try {
			const result = yield CoCo.searchByPerson(actor, genre, false)
			let contents = result.getContentsRaw()
			// CoCo returns string instead of object, need to convert to object
         if (typeof contents === "string") contents = JSON.parse(contents)
			result.contents = contents
			console.debug("searchActorInGenreAsync actor %s, genre %s, result %o, len %s", actor, genre, result, result.contents.length)
			return result
      }
		catch (e) {
			console.error("searchActorInGenreAsync", e)
         return {}
      }
   })

   // requires exact match, otherwise 404
   searchGenreInGenreAsync = flow(function* (genre, subGenre) {
      // exception for 404 might be generated
      try {
			const result = yield CoCo.searchByGenre(genre, subGenre, false)
			let contents = result.getContentsRaw()
			// CoCo returns string instead of object, need to convert to object
         if (typeof contents === "string") contents = JSON.parse(contents)
			result.contents = contents
         console.debug("searchGenreInGenreAsync genre %s, subGenre %s, result %o, len %s", genre, subGenre, result, result.contents.length)
			return result
      }
		catch (e) {
			console.error("searchGenreInGenreAsync", e)
         return {}
      }
   })

	// MTVW-1016
	seriesEpisodesAsync = flow(function* (seriesId) {
		try {
			//CoCo.setFeatureFlagEnabled(CoCo.FeatureFlag.SearchRecordingsEnabled, true)
			//CoCo.setFeatureFlagEnabled(CoCo.FeatureFlag.SearchRecordingsWithSeriesResultsEnabled, true)
			const episodes = yield CoCo.searchBySeries(seriesId)
			console.debug("seriesEpisodesAsync", episodes)
			return episodes
		}
		catch (e) {
			// handled by caller
			throw (e)
      }
	})
}
